import { Component, EventEmitter, Input, Output, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTimes, faUpload, faFile, faDownload, faTrash, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { Partner } from '../../../../../core/models/partner.model';
import { PartnerFile } from '../../../../../core/models/partner-file.model';
import { PartnerService } from '../../../../../core/services/partner.service';
import { PopupInfoService } from '../../../../../shared/services/popup-info.service';

@Component({
  selector: 'app-partner-files-modal',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule],
  templateUrl: './partner-files-modal.html',
})
export class PartnerFilesModalComponent implements OnInit {
  @Input() partner!: Partner;
  @Output() close = new EventEmitter<void>();

  private readonly partnerService = inject(PartnerService);
  private readonly popupInfoService = inject(PopupInfoService);

  // Icons
  faTimes = faTimes;
  faUpload = faUpload;
  faFile = faFile;
  faDownload = faDownload;
  faTrash = faTrash;
  faSpinner = faSpinner;

  files: PartnerFile[] = [];
  isLoading = false;
  isUploading = false;

  ngOnInit(): void {
    this.loadFiles();
  }

  loadFiles(): void {
    this.isLoading = true;
    this.partnerService.getFiles(this.partner.id).subscribe({
      next: (files: PartnerFile[]) => {
        this.files = files;
        this.isLoading = false;
      },
      error: () => {
        this.popupInfoService.showError('Error al cargar los archivos');
        this.isLoading = false;
      }
    });
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.uploadFile(file);
    }
  }

  uploadFile(file: File): void {
    this.isUploading = true;
    this.partnerService.uploadFile(this.partner.id, file).subscribe({
      next: (newFile: PartnerFile) => {
        this.files.unshift(newFile);
        this.isUploading = false;
        this.popupInfoService.showSuccess('Archivo subido correctamente');
      },
      error: () => {
        this.popupInfoService.showError('Error al subir el archivo');
        this.isUploading = false;
      }
    });
  }

  downloadFile(file: PartnerFile): void {
    this.partnerService.downloadFile(file).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      },
      error: () => this.popupInfoService.showError('Error al descargar el archivo')
    });
  }

  deleteFile(fileId: number): void {
    if (confirm('¿Estás seguro de que quieres eliminar este archivo?')) {
      this.partnerService.deleteFile(fileId).subscribe({
        next: () => {
          this.files = this.files.filter(f => f.id !== fileId);
          this.popupInfoService.showSuccess('Archivo eliminado');
        },
        error: () => this.popupInfoService.showError('Error al eliminar el archivo')
      });
    }
  }

  formatBytes(bytes: number = 0): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}
